<template>
  <div class="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8" v-if="form">
    <div class="sm:mx-auto sm:w-full sm:max-w-sm">
      <router-link :to="{ name: 'landing' }">
        <img class="mx-auto w-auto"
             src="/images/logos/haldex.png"
             alt="Haldex"/>
      </router-link>
      <h2 class="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
        {{ step === 'login' ? $t('pages.auth.login_view.sign_in_to_your_account') : '' }}
        {{ step === '2fa' ? $t('pages.auth.login_view.enter_2fa_code') : '' }}
        {{ step === 'recovery' ? 'Account Recovery' : '' }}
      </h2>
    </div>

    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
      <!-- Login Form -->
      <form class="space-y-6" @submit.prevent="submitLogin">
        <div v-show="step === 'login'">
          <FormGroup :label="$t('pages.auth.login_view.email_address')" :error-text="form.errors?.email">
            <InputText v-model="form.email" id="email" name="email" type="email" autocomplete="email" data-cy="login-email" required />
          </FormGroup>

        <FormGroup :label="$t('pages.auth.login_view.password')" :error-text="form.errors?.password">
          <Password :feedback="false" v-model="form.password" id="password" name="password"
                    autocomplete="current-password"
                    required
                    toggleMask
                    input-class="w-full"
                    data-cy="login-password"
          />
        </FormGroup>

          <div>
            <br />
            <Button type="submit" :label="$t('pages.auth.login_view.sign_in')" class="w-full" data-cy="login-submit" />
          </div>

          <div v-if="showSSOOption" class="mt-4">
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300" />
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>
            
            <div class="mt-4">
              <Button 
                type="button" 
                @click="loginWithMicrosoft"
                class="w-full bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
                severity="info"
              >
                <template #icon>
                  <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
                  </svg>
                </template>
                Login with Microsoft
              </Button>
            </div>
          </div>
        </div>

        <!-- Two-Factor Authentication Form -->
        <div v-show="step === '2fa'" class="space-y-6">
          <FormGroup :label="$t('pages.auth.login_view.2fa_code')" :error-text="form.errors?.code">
            <!-- <InputText v-model="form.two_fa_code_split" name="two_fa_code_split" id="code" type="text" /> -->
             
                    
            <div class="flex justify-center gap-2 my-12">
                <input
                    v-for="(box, index) in 6"
                    :key="index"
                    v-model="two_fa_code_split[index]"
                    type="text"
                    maxlength="1"
                    class="w-12 h-12 text-center text-xl border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    @input="handleInput($event, index)"
                    @paste="handlePaste($event)"
                    @focus="this.codeErrors=null; $event.target.select()"
                    ref="inputBoxes"
                    data-cy="2fa-code"
                />
                <!-- <input type="text" v-model="form.two_fa_code" /> -->
            </div>
          </FormGroup>

          <div>
            <Button type="submit" :label="$t('pages.auth.login_view.verify_2fa')" @click="submit2FA()" class="w-full" data-cy="2fa-submit" />
          </div>
          <div class="mt-4 text-center">
            <Button @click="showRecoveryCode()" link label="Lost access to your authenticator? Use a recovery code" class="text-sm" />
          </div>
        </div>
      </form>
      <!-- <br />
      <br />
      <Button type="button" label="(debug) Remove 2FA" class="w-full" @click="remove2FA" /> -->
        <div v-show="step === 'recovery'" class="space-y-6">
          <p class="text-gray-600 text-sm">
            Enter one of the one-time use recovery codes you saved when you first set up 2FA for this account.
          </p>
          <p class="text-gray-500 text-xs">
            Note: Each recovery code can only be used once.<br />
            If you have lost access to your authenticated device, please disable 2FA and setup 2FA again once you've logged back in.
          </p>

          <input
              v-model="recovery_code"
              type="text"
              placeholder="xxxx-xxxx-xxxx"
              class="mt-4 w-full h-12 text-center text-xl border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              ref="recoveryCodeInput"
              @focus="recoveryErrors = null"
          />
          <div class="text-red-500 text-center text-sm" v-if="recoveryErrors">{{ recoveryErrors }}</div>
          <div class="space-y-2">
            <Button label="Submit Recovery Code" @click="submitRecoveryCode()" class="w-full" :loading="recoveryLoading" />
            <Button label="Back" @click="step = '2fa'" severity="secondary" outlined class="w-full" />
          </div>
        </div>

      <p v-if="step === 'login'" class="mt-8 text-center">
        <router-link :to="{ name: 'password.request' }">
          <Button :label="$t('pages.auth.login_view.ive_forgotten_my_password')" link />
        </router-link>
      </p>
          </div>
  </div>
  
  <!-- Version Number Display -->
  <div class="fixed bottom-4 right-4 text-xs text-gray-400 bg-white/80 backdrop-blur-sm px-2 py-1 rounded shadow-sm">
    v{{ version }}
  </div>
</template>

<script>
import axios from "axios";
import FormGroup from "@/Components/FormGroup.vue";

export default {
  components: { FormGroup },
  data() {
    return {
      form: null,
      userId: null,
      step: "login", // Tracks login step: 'login' -> '2fa' -> 'recovery'
      two_fa_code_split: [],
      two_fa_code: '',
      codeErrors: null,
      recovery_code: '',
      recoveryErrors: null,
      recoveryLoading: false,
      version: APPLICATION_VERSION, // Dynamic version from composer.json via Vite
    };
  },
  computed: {
    showSSOOption() {
      return this.$route.query.sso !== undefined;
    }
  },
  // Ensure locale is set before template renders
  beforeCreate() {
    const saved = localStorage.getItem('user-locale') || (navigator.language || 'en').slice(0, 2);
    if (this.$i18n && this.$i18n.locale !== saved) {
      this.$i18n.locale = saved;
    }
    document.documentElement.setAttribute('lang', saved);
    axios.defaults.headers.common['Accept-Language'] = saved;
  },
  async mounted() {
    this.form = await this.$auth.getLoginUserForm();
  },
  methods: {
    async submitLogin() {
      try {
        const response = await this.form.submit();

        // If login is successful without 2FA, proceed
        console.log(response.data);
        // should return data and token
        if(response.data.token){
          this.$auth.loginUser({ user: response.data.data, token: response.data.token });        
          this.$router.go(0);
        }else if(response.data.two_factor){
          this.userId=response.data.user_id;
          this.step = '2fa';
          this.$nextTick(() => {
              if (this.$refs.inputBoxes && this.$refs.inputBoxes[0]) {
                  this.$refs.inputBoxes[0].focus();
              }
          });
        }else{
          console.log('error logging in');
        }
      } catch (e) {
        console.log(e.response.data.message);
        // If login requires 2FA, Fortify responds with a 422 status
        if(e.response.data.message){
            this.codeErrors = e.response.data.message;
        }else if (e.response && e.response.status === 422) {
          this.step = "2fa"; // Switch to 2FA form
          this.$refs.inputBoxes[0].focus();
        // this.$toast.add({
        //   severity: 'success',
        //   summary: this.$t('common.complete'),
        //   detail: 'Your account requires Two Factor Authentication',
        //   life: 3000
        // });
        } else {
          console.error("Login failed", e);
        }
      }
    },
    async submit2FA() {
      try {
        this.two_fa_code = this.two_fa_code_split.join('');
        this.form.two_fa_code = this.two_fa_code;
        // const response2 = await axios.post("user/verify-2fa", {
        //   user_id: this.userId,
        //   code: this.two_fa_code,
        // });

        // If 2FA is successful, log in the user
        // if(response2.data == 'success'){
          // alert('success again');
          // this.$auth.loginUser({ user: this.userId, token: response.data.token });
          // this.$router.go(0);
          this.form.two_fa_code = this.two_fa_code;
          this.submitLogin();
        // }else{
        //   console.log('ere');
        // }
      } catch (error) {
        // console.error("2FA verification failed", e);
        if(error.response.data.errors && error.response.data.errors['code']){
            this.codeErrors = error.response.data.errors['code'][0];
        }
      }
    },
    async remove2FA() {
      try {
        const response = await axios.delete("user/two-factor-authentication/57", {
          // user_id: this.userId,
        });

        // If 2FA is successful, log in the user
        alert("2FA deleted");
      } catch (e) {
        console.error("2FA delete failed", e);
      }
    },
    showRecoveryCode(){
      this.step = 'recovery';
      this.recovery_code = '';
      this.recoveryErrors = null;
      this.$nextTick(() => {
        if (this.$refs.recoveryCodeInput) {
          this.$refs.recoveryCodeInput.focus();
        }
      });
    },
    async submitRecoveryCode(){
      this.recoveryLoading = true;
      this.recoveryErrors = null;

      try {
        const response = await axios.post('auth/recover-2fa', {
          email: this.form.email,
          password: this.form.password,
          recovery_code: this.recovery_code,
        });

        if (response.data.token) {
          this.$auth.loginUser({ user: response.data.data, token: response.data.token });
          this.$router.go(0);
        }
      } catch (error) {
        if (error.response?.data?.errors?.recovery_code) {
          this.recoveryErrors = error.response.data.errors.recovery_code[0];
        } else if (error.response?.data?.message) {
          this.recoveryErrors = error.response.data.message;
        } else {
          this.recoveryErrors = 'Failed to verify recovery code. Please try again.';
        }
      } finally {
        this.recoveryLoading = false;
      }
    },
    backToLogin(){
      this.step='login';
    },
    loginWithMicrosoft() {
      // Redirect to Microsoft OAuth endpoint
      // This would typically redirect to your backend's Microsoft OAuth route
      window.location.href = '/auth/microsoft';
    },  
    handleInput(event, index) {
        const value = event.target.value;
        if (value.length > 1) {
            this.two_fa_code_split[index] = value[1];
        }
        if (value.length === 0) {
            this.two_fa_code_split[index] = "";
        }
        if (index < 5 && value.length === 1) {
            this.$refs.inputBoxes[index + 1].focus();
        }
        if(index == 5){
            // validate when last box is filled
            // this.validateTwoFactorCode();
        }
    },
    handlePaste(event) {
        event.preventDefault();
        const paste = event.clipboardData.getData("text");
        if (paste.length === 6 && /^\d+$/.test(paste)) {
            this.two_fa_code_split = paste.split("");
            this.$refs.inputBoxes[0].blur();
            // this.validateTwoFactorCode();
        }
    },
  },
};
</script>

<style scoped></style>
